'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    id: '1',
    question: 'Hvordan vet jeg at jeg får en rettferdig pris?',
    answer: 'Vi baserer våre priser på dagens internasjonale gullpris. Du får pristilbud på stedet og kan sammenligne med andre før du bestemmer deg. Ingen forpliktelser.',
  },
  {
    id: '2',
    question: 'Er hjemmebesøket trygt?',
    answer: 'Ja, absolutt. Våre medarbeidere er profesjonelle og diskrete. Du velger selv tidspunkt som passer deg, og du bestemmer om du vil selge eller ikke. Ingen press eller forpliktelser.',
  },
  {
    id: '3',
    question: 'Hvor lang tid tar prosessen?',
    answer: 'Selve besøket tar vanligvis 15-30 minutter. Du får vurdering og pristilbud på stedet, og oppgjør skjer umiddelbart hvis du aksepterer tilbudet.',
  },
  {
    id: '4',
    question: 'Hva skjer hvis jeg ikke er fornøyd med tilbudet?',
    answer: 'Du bestemmer selv om du vil selge. Hvis du ikke er fornøyd med tilbudet, beholder du gullet ditt. Ingen spørsmål stilt, ingen kostnader.',
  },
  {
    id: '5',
    question: 'Må jeg betale skatt på pengene jeg får?',
    answer: 'Salg av personlige eiendeler som smykker er normalt ikke skattepliktig i Norge. For arvegull eller større mengder anbefaler vi at du konsulterer en skatterådgiver. Vi kan gi deg dokumentasjon for transaksjonen.',
  },
  {
    id: '6',
    question: 'Hvilke typer gull kjøper dere?',
    answer: 'Vi kjøper alle typer gull: smykker, mynter, barrer, tannkroner, og brutte smykker. Vi aksepterer gull fra 9 karat og oppover. Selv ødelagte eller ufullstendige smykker har verdi.',
  },
  {
    id: '7',
    question: 'Trenger jeg dokumentasjon for gullet mitt?',
    answer: 'For de fleste transaksjoner trenger du bare gyldig legitimasjon. For arvegull eller større mengder kan vi be om ekstra dokumentasjon i henhold til hvitvaskingsloven.',
  },
  {
    id: '8',
    question: 'Hvordan vet jeg at dere er seriøse?',
    answer: 'Vi jobber etter faste rutiner for sikker håndtering og tydelig kommunikasjon. Du møter oss personlig, og kan når som helst ta kontakt med spørsmål.',
  },
];

const FAQSection = () => {
  const [openItems, setOpenItems] = useState<string[]>([]);

  const toggleItem = (id: string) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <section className="bg-muted py-12 lg:py-20">
      <div className="mx-auto max-w-4xl px-5 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
            Ofte stilte spørsmål
          </h2>
          <p className="mt-4 font-body text-lg text-muted-foreground lg:text-xl">
            Vi har samlet svar på de vanligste spørsmålene våre kunder stiller
          </p>
        </div>

        <div className="space-y-4">
          {faqItems.map((item) => {
            const isOpen = openItems.includes(item.id);
            return (
              <div
                key={item.id}
                className="overflow-hidden rounded-lg bg-card shadow-md transition-all duration-300"
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="flex w-full items-center justify-between p-6 text-left transition-colors duration-250 hover:bg-muted"
                >
                  <span className="font-headline text-lg font-semibold text-foreground lg:text-xl">
                    {item.question}
                  </span>
                  <Icon
                    name={isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={24}
                    variant="outline"
                    className="flex-shrink-0 text-primary transition-transform duration-250"
                  />
                </button>
                {isOpen && (
                  <div className="border-t border-border px-6 pb-6 pt-4">
                    <p className="font-body text-base text-muted-foreground">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-lg bg-primary/10 p-6 text-center lg:p-8">
          <Icon name="QuestionMarkCircleIcon" size={48} variant="solid" className="mx-auto mb-4 text-primary" />
          <h3 className="font-headline text-xl font-semibold text-foreground lg:text-2xl">
            Har du flere spørsmål?
          </h3>
          <p className="mt-2 font-body text-base text-muted-foreground">
            Vårt kundeserviceteam er klare til å hjelpe deg. Kontakt oss på telefon, e-post eller chat.
          </p>
          <a
            href="#kontakt"
            className="mt-6 inline-flex items-center space-x-2 rounded-lg bg-accent px-6 py-3 font-cta text-base font-semibold text-accent-foreground shadow-cta transition-all duration-250 hover:bg-accent/90"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} variant="outline" />
            <span>Kontakt oss</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;